/**
 * Лёгкий тактильный отклик на значимые нажатия: отметка «готово», шаг статуса,
 * быстрая запись, подтверждённое удаление. Не на каждой кнопке — отклик,
 * который звучит на всём подряд, перестаёт что-либо значить и начинает
 * раздражать.
 *
 * Два пути, потому что единого нет:
 *
 * - Android и всё, где есть Vibration API, — `navigator.vibrate`.
 * - iPhone: WebKit Vibration API не реализует вовсе. Зато с iOS 18 нативный
 *   переключатель `<input type="checkbox" switch>` при переключении даёт
 *   системный щелчок — его и нажимаем через подпись. Поведение не
 *   документировано: если Apple его уберёт, отклик молча пропадёт, а не
 *   сломает нажатие.
 *
 * Вызывать строго синхронно внутри обработчика нажатия: iOS отдаёт щелчок
 * только в пределах жеста человека, после `await` или таймера его уже нет.
 *
 * Переключатель живёт под `display: none` и сразу удаляется: такой элемент не
 * получает фокус, и клавиатура телефона не закрывается посреди быстрой записи.
 */
/**
 * Сила отклика. Сам щелчок iOS не усилить: силу задаёт система, и из веба
 * доступен только лёгкий «тик» (замечание владельца 2026-10-17: «очень
 * незаметная»). Единственный рычаг — повтор: два тика подряд ощущаются
 * заметно сильнее. `strong` — для событий, которые человек должен узнать без
 * взгляда на экран: строка взята, свайп сработал.
 */
export type HapticStrength = 'tick' | 'strong';

/** Пауза между тиками двойного: короче — сливаются в один, длиннее — два отдельных. */
const DOUBLE_GAP_MS = 70;

export function haptic(strength: HapticStrength = 'tick'): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(strength === 'strong' ? [12, DOUBLE_GAP_MS, 12] : 10);
    return;
  }
  if (typeof document === 'undefined') return;

  tick();
  // Второй тик — по таймеру, вне жеста. Если iOS его не пропустит, останется
  // обычный одинарный: хуже, чем было, не станет.
  if (strength === 'strong') setTimeout(tick, DOUBLE_GAP_MS);
}

function tick(): void {
  const label = document.createElement('label');
  label.setAttribute('aria-hidden', 'true');
  label.style.display = 'none';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('switch', '');
  input.tabIndex = -1;
  label.append(input);

  document.head.append(label);
  try {
    label.click();
  } finally {
    label.remove();
  }
}
